import { useState, useEffect } from "react";
import { CardA } from "../../ui/Cards";
import { useConnectorCalibrate, useServerCalibrate } from "../../../api";
import { useSampleContext } from "../../../contexts";

const WizardCalibrateScan = ({ step, changeStep, pageID }) => {
  const { device } = useSampleContext();
  const connectorCalibration = useConnectorCalibrate(
    device.deviceId,
    onConnectorSuccess,
    (err) => errorLogger("connector", err)
  );
  const serverCalibration = useServerCalibrate(
    device.deviceId,
    Date.now(),
    onServerSuccess,
    (err) => errorLogger("server", err)
  );
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState("");

  // Clear previous calibration error when mounting this step
  useEffect(() => {
    if (step === pageID) {
      setInlineError("");
      sessionStorage.removeItem("calibrationErrorMessage");
      sessionStorage.removeItem("calibrationErrorStage");
    }
  }, [step, pageID]);

  function errorLogger(stage, err) {
    setLoading(false); // deactivate loading spinner
    // Extract readable message
    const message =
      err?.response?.data?.error?.message ||
      err?.response?.data?.message ||
      err?.message ||
      (stage === "connector"
        ? "Connector calibration request failed. Ensure the Connector App is running and device is connected."
        : "Server calibration save failed. Please retry.");
    console.error("Calibration error (" + stage + "):", err);

    // Store for fail screen consumption
    sessionStorage.setItem("calibrationErrorMessage", message);
    sessionStorage.setItem("calibrationErrorStage", stage);

    // Show small inline helper if user remains here (fast failure before navigation)
    setInlineError(message);
    changeStep("fail");
  }

  // Callback used when the connector query finishes to check the results
  function onConnectorSuccess(data) {
    if (data) {
      // Check the result response code of the connector
      if (data.status && data.status >= 200 && data.status < 300) {
        // only 200s pass
        console.log("Connector calibration successful, query the server next.");
        serverCalibration.refetch();
      } else {
        console.log("Connector calibration failed.");
        errorLogger("connector", {
          message:
            data?.message ||
            "Unexpected status code from connector: " + data.status,
        });
      }
    } else {
      console.log("No connector calibration data found.");
      errorLogger("connector", { message: "No data returned from connector." });
    }
  }

  // Callback used when the server query finishes to check the results
  function onServerSuccess(data) {
    if (data) {
      setLoading(false); // deactivate loading spinner
      if (data.status && data.status >= 200 && data.status < 300) {
        // only 200s pass
        console.log("Server calibration successful. Results saved.");
        changeStep("success");
      } else {
        console.log("Server calibration failed.");
        errorLogger("server", {
          message: "Unexpected status code from server: " + data.status,
        });
      }
    } else {
      console.log("No server calibration data found.");
      errorLogger("server", { message: "No data returned from server." });
    }
  }

  if (step !== pageID) {
    return null;
  }

  // Gets the calibration response from the connector app, then saves the
  // date and time (if it was successful) to the server
  const calibrateDevice = () => {
    if (device.deviceId) {
      setLoading(true); // activate loading spinner
      setInlineError("");
      connectorCalibration.refetch();
    } else {
      console.error("WizardCalibrateScan.js - No device id to calibrate.");
      setInlineError("No device selected. Please return and choose a device.");
    }
  };

  return (
    <CardA
      title='Calibration Scan'
      subtitle={`Keep the calibration chip in the reader for "${device.name}".`}
    >
      <div className='row mt-5'>
        {inlineError && (
          <div className='col-12 mb-3'>
            <div
              className='alert alert-danger alert-dismissible fade show'
              role='alert'
            >
              <i className='bi bi-exclamation-triangle-fill me-2'></i>
              <strong>Calibration Error:</strong> {inlineError}
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                onClick={() => setInlineError("")}
              ></button>
            </div>
          </div>
        )}
        <button
          type='button'
          className='col-4 btn btn-secondary mx-auto w-25'
          onClick={() => changeStep("return")}
          disabled={loading}
        >
          Back
        </button>
        <button
          type='button'
          className='btn btn-success col-4 mx-auto w-25'
          onClick={calibrateDevice}
          disabled={loading}
        >
          {loading ? (
            <span>
              <span
                className='spinner-border spinner-border-sm'
                aria-hidden='true'
              ></span>
              <span> Loading...</span>
            </span>
          ) : (
            <span>Start Calibration</span>
          )}
        </button>
      </div>
    </CardA>
  );
};

export default WizardCalibrateScan;
